/**
 * Finish a rotation this device holds the pending generation for: walk what is
 * left onto the new key, then commit. ONE definition, used by the Sign out
 * screen and by the launch resume, so the two cannot finish differently.
 *
 * ⚠️ SINGLE-FLIGHT PER STORE. Both callers can be live at once — the resume
 * installed at launch and the screen's own `mounted` — and two walks racing
 * would spend two nonces on one commit and fail the loser as a rejected
 * signature. A second caller joins the first and gets the same answer.
 *
 * Never rejects on a walk that could not finish: that is an OUTCOME the member
 * can act on (`ROWS_REMAIN`, with what is stuck), not an error. A commit that
 * the server refuses still throws, as it always has, so a caller can show the
 * code.
 */
import { commitRotation } from './rotation';
import { resealRemaining } from './rotation-walk';

/**
 * How many of each kind are in a `type:id` list — the shape a screen can put
 * into a sentence. Here and not in the walk, because this is the module that
 * turns a walk's result into something a caller reads, and because the Sign
 * out page's spec mocks the walk module wholesale.
 *
 * @param {string[]} stuck `type:id` entries, in this build's own type names
 * @returns {Record<string, number>} counts keyed by `ANNOTATION_TYPE` value
 */
export function summariseStuck(stuck) {
  return (stuck ?? []).reduce((counts, entry) => {
    const type = String(entry).split(':')[0];
    return { ...counts, [type]: (counts[type] ?? 0) + 1 };
  }, {});
}

export const FINISH_OUTCOME = Object.freeze({
  DONE: 'done',
  ROWS_REMAIN: 'rows-remain',
  /**
   * The server is rotating and this device does not hold the pending
   * generation, so it cannot write a single row under it. `startedHere` says
   * whether this is the phone that BEGAN the rotation and has since lost the
   * key — the case the recovery code exists for — or another device's
   * rotation, which is not this one's to touch.
   */
  NOT_THIS_DEVICE: 'not-this-device',
});

const DEFAULT_DEPS = Object.freeze({ resealRemaining, commitRotation });

/** The run in flight for each store, so a second caller joins rather than races. */
const inFlight = new WeakMap();

/**
 * @param {{ store: object, onProgress?: (progress: { done: number, total: number }) => void, deps?: object }} args
 * @returns {Promise<{ outcome: string, remaining: number, stuck: string[], unreachable: number, byType: Record<string, number> }>}
 */
export function finishRotation({
  store,
  onProgress = () => {},
  carryUnrecoverable = false,
  deps: overrides,
} = {}) {
  if (!store) throw new TypeError('finishRotation needs the root Vuex store');
  const running = inFlight.get(store);
  if (running) return running;

  const deps = overrides ? { ...DEFAULT_DEPS, ...overrides } : DEFAULT_DEPS;
  const run = (async () => {
    /*
     * ⚠️ HOLDING THE PENDING KEY IS CHECKED BEFORE A SINGLE WRITE. The walk
     * checked only that the SERVER had a rotation pending; on a phone holding
     * MK1 alone it collected every row, failed to seal each one under a key it
     * did not have (ROTATION_IN_PROGRESS, per row), moved nothing, and reported
     * "some notes are still being moved — tap again", which could never work.
     * The one check that said the truth sat in the commit path the walk never
     * reached. Reported on 2026-09-10 as a "Continue signing out" that looped.
     */
    const pendingKeyId = store.getters['courses-devices/pendingKeyId'];
    const held = store.getters['courses-keys/pendingKeyMaterial'];
    if (pendingKeyId && held?.keyId !== pendingKeyId) {
      const rotation = store.getters['courses-devices/rotation'];
      const startedHere =
        !!rotation?.deviceId &&
        rotation.deviceId === store.getters['courses-devices/localDeviceId'];
      return { outcome: FINISH_OUTCOME.NOT_THIS_DEVICE, startedHere };
    }
    const walked = await deps.resealRemaining({ store, onProgress, carryUnrecoverable });
    const stuck = walked.stuck ?? [];
    const detail = {
      remaining: walked.remaining ?? 0,
      stuck,
      unreachable: walked.unreachable ?? 0,
      missing: walked.missing ?? 0,
      unreadable: walked.unreadable ?? 0,
      unwritable: walked.unwritable ?? 0,
      unrecoverable: walked.unrecoverable ?? 0,
      needsNewerApp: walked.needsNewerApp ?? 0,
      carried: walked.carried ?? 0,
      reasons: walked.reasons ?? {},
      byType: summariseStuck(stuck),
    };
    if (detail.remaining > 0) return { outcome: FINISH_OUTCOME.ROWS_REMAIN, ...detail };
    const committed = await deps.commitRotation({ store });
    return {
      outcome: FINISH_OUTCOME.DONE,
      ...detail,
      // Which other devices were handed the new key, and which the member
      // asked for: the done screen says so, because the difference between
      // the two is a device that will be asking for approval.
      kept: committed?.kept ?? [],
      keptAsked: committed?.asked ?? [],
    };
  })().finally(() => {
    if (inFlight.get(store) === run) inFlight.delete(store);
  });
  inFlight.set(store, run);
  return run;
}
